<script setup>
import GenericChip from 'components/GenericChip.vue';
import LocationChip from 'components/LocationChip.vue';
import { useSearchManager } from 'composables/search-manager';
import { useClassificationStore } from 'stores/classification-store';
import { useJobBrowseStore } from 'stores/job-browse-store';
import { useJobSearchStore } from 'stores/job-search-store';
import { computed, onBeforeMount } from 'vue';

const classificationStore = useClassificationStore();
const jobBrowseStore = useJobBrowseStore();
const jobSearchStore = useJobSearchStore();
const searchManager = useSearchManager();

const isClassificationsLoaded = computed(() => classificationStore.classifications.length > 0);
const isDivisionsLoaded = computed(() => jobBrowseStore.divisions.length > 0);

onBeforeMount(() => {
    classificationStore.loadAllClassifications();
    jobBrowseStore.loadCountry()
        .then(() => jobBrowseStore.loadAllCountryDivisions());
});

async function performSearch(searchType, searchValue) {
    jobSearchStore.resetSearchFilters();
    const filters = createSearchFilters(searchType, searchValue);
    await searchManager.createNewSearch(filters);
}

function getSearchRouteUrl(searchType, searchValue) {
    const filters = createSearchFilters(searchType, searchValue);
    return searchManager.buildSearchRoute(filters).url;
}

function createSearchFilters(searchType, searchValue) {
    switch (searchType) {
        case 'classification':
            return {
                classificationId: [searchValue.id],
                location: jobBrowseStore.country
            };
        case 'location':
            return {
                location: searchValue
            };
        default:
            throw new Error('Invalid search type');
    }
}
</script>

<template>
    <div class="browse-jobs-section full-width bg-card q-mt-xl q-py-xl">
        <div class="container-md q-px-md">
            <h3 class="q-mt-none">Browse Jobs</h3>
            <p class="text-subtitle1 q-mt-md q-mb-md">by State</p>
            <div class="q-mx-n-xs">
                <template v-if="isDivisionsLoaded">
                    <a
                        v-for="location in jobBrowseStore.divisions"
                        :key="location.id"
                        @click.prevent="performSearch('location', location)"
                        :href="getSearchRouteUrl('location', location)"
                        :title="`Browse Jobs in ${location.commonName}`"
                    >
                        <LocationChip :location clickable>
                            <span class="hidden">Browse Jobs in </span>{{ location.commonName }}
                        </LocationChip>
                    </a>
                </template>
                <template v-else>
                    <q-skeleton
                        v-for="n in 7"
                        :key="n"
                        type="QChip"
                        :width="Math.floor(Math.random() * (205 - 76 + 1) + 76) + 'px'"
                        class="q-chip rounded-borders inline-block"
                        square
                    />
                </template>
            </div>
            <p class="text-subtitle1 q-mt-md q-mb-md">by Classification</p>
            <div class="q-mx-n-xs">
                <template v-if="isClassificationsLoaded">
                    <a
                        v-for="classification in classificationStore.classifications"
                        :key="classification.id"
                        @click.prevent="performSearch('classification', classification)"
                        :href="getSearchRouteUrl('classification', classification)"
                        :title="`Browse Jobs in ${classification.name}`"
                    >
                        <GenericChip clickable>
                            <span class="hidden">Browse Jobs in </span>{{ classification.name }}
                        </GenericChip>
                    </a>
                </template>
                <template v-else>
                    <q-skeleton
                        v-for="n in 40"
                        :key="n"
                        type="QChip"
                        :width="Math.floor(Math.random() * (206 - 60 + 1) + 60) + 'px'"
                        class="rounded-borders inline-block q-my-none"
                        square
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.browse-jobs-section {
    border-bottom: 1px solid rgba($grey-3, 0.6);
    border-top: 1px solid rgba($grey-3, 0.6);
}
</style>
